import { Features, type FeatureName } from '../types/features';
import type { ToolConfig } from '../contexts/TenantConfigContext';

export const STATIC_TOOL_CONFIGS: Record<FeatureName, ToolConfig> = {
  [Features.AUDIO_TRANSCRIPTION]: {
    description: 'Upload a recording and generate a polished transcript with optional diarization and dictation cleanup.',
    documentGuidance: {
      title: 'What to Upload',
      shortDescription: 'Best for interviews, dictated notes, meetings, and call recordings.',
      acceptedFormats: ['MP3', 'MP4', 'M4A', 'WAV', 'FLAC', 'WEBM', 'OGG', 'OPUS'],
      recommendedDocuments: [
        'A single clear audio recording',
        'Speaker names if you want labelled diarization',
        'Any domain-specific vocabulary that should be preserved',
      ],
      tips: [
        'Use one recording per run for the most reliable speaker handling.',
        'Enable AI correction when you want cleaned-up output instead of verbatim raw text.',
      ],
    },
  },
  [Features.TEXT_TRANSFORM]: {
    description: 'Turn rough notes or source documents into emails, letters, summaries, updates, or custom rewritten text.',
    documentGuidance: {
      title: 'What to Upload',
      shortDescription: 'Works with source documents, rough notes, or text you want rewritten into a target format.',
      acceptedFormats: ['PDF', 'DOCX', 'EML', 'MSG', 'ZIP'],
      recommendedDocuments: [
        'Source document or notes to transform',
        'Optional extra context for tone or audience',
        'A clear transform goal such as summary, email, or custom instruction',
      ],
      tips: [
        'Use custom instructions when you need a specific tone, structure, or audience.',
        'Add supporting files if the rewrite needs reference material.',
      ],
    },
  },
  [Features.QA_ASSIST]: {
    description: 'Ask targeted questions against uploaded source material and get a grounded answer from the document set.',
    documentGuidance: {
      title: 'What to Upload',
      shortDescription: 'Best for reviewing policy documents, correspondence, reports, or bundled matter files.',
      acceptedFormats: ['PDF', 'DOCX', 'EML', 'MSG', 'ZIP'],
      recommendedDocuments: [
        'Primary source documents that contain the answer',
        'Supporting material that adds context',
        'A precise question or list of questions',
      ],
      tips: [
        'Ask focused questions for the most reliable grounded answers.',
        'Include all relevant supporting documents in one run if the answer spans multiple files.',
      ],
    },
  },
  [Features.FORM_FILLER]: {
    description: 'Populate a target form using uploaded evidence and supporting documents, with AI-assisted field mapping.',
    documentGuidance: {
      title: 'What to Upload',
      shortDescription: 'Upload the form to complete plus the source material the model should use to populate it.',
      acceptedFormats: ['PDF', 'DOCX', 'ZIP'],
      recommendedDocuments: [
        'The target form or template to populate',
        'Supporting source documents containing the required answers',
        'Any special instructions for how uncertain fields should be handled',
      ],
      tips: [
        'Use fillable PDFs or DOCX templates where possible for best output quality.',
        'Include the most authoritative source documents first if several files overlap.',
      ],
    },
  },
};
